import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Download as DownloadIcon,
  EventBusy as EventBusyIcon,
  FolderOutlined as ProjectIcon,
  LocalOfferOutlined as ActivityIcon,
  TableChart as TableIcon,
} from '@mui/icons-material';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useCurrentUser } from '../hooks/useCurrentUser';
import MonthPicker from '../components/monthly/MonthPicker';
import PageHeader, { PageContainer } from '../components/PageHeader';
import MultiSelectFilter, { barButtonSx, TogglePill } from '../components/monthly/MultiSelectFilter';
import { MUTED as MUTED_TEXT } from '../components/home/homeTheme';
import MonthlyPivotTable from '../components/monthly/MonthlyPivotTable';
import { buildMonthDays, formatHours } from '../components/monthly/format';
import { downloadHoursWorkbook } from '../components/monthly/exportHours';
import { fetchProjectDailyMinutes, fetchProjectPeople, fetchVisibleProjects } from '../utils/monthlyManagerService';
import { getAllAbsencesForMonth } from '../utils/absenceService';
import { excludeForecastActivities } from '../utils/worklogFilters';
import {
  downloadAbsenceCalendarWorkbook,
  downloadAllProjectsAbsenceCalendarWorkbook,
  sortMembersForCalendar,
} from '../utils/absenceCalendarExport';
import { API_ENDPOINTS } from '../config/api.config';

const DELIVERY_MANAGER_ROLE_ID = 7;

/** Ruolo di gestione sul progetto (Service Manager / JR SM), come nella vista classica. */
function isManagerForProject(project, isAdmin) {
  if (!project.project_role) return isAdmin;
  if (String(project.project_role.code || '').toUpperCase() === 'T') return false;
  return [1, 2].includes(project.project_role.id);
}

export default function MonthlyManagerViewV2() {
  const { isAdmin, isServiceManager, isTeamLeader, isJRServiceManager, isDeliveryManager, currentRole } = useRoleAccess();
  const { userId } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const now = new Date();
  const [year, setYear] = useState(() => Number(searchParams.get('year')) || now.getFullYear());
  const [month, setMonth] = useState(() => Number(searchParams.get('month')) || now.getMonth() + 1);
  const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
  const monthDays = useMemo(() => buildMonthDays(year, month), [year, month]);

  const isDM = isDeliveryManager || currentRole === DELIVERY_MANAGER_ROLE_ID;
  const seesManagedProjects = !isDM && (isAdmin || isServiceManager || isTeamLeader || isJRServiceManager);

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [projectIds, setProjectIds] = useState([]);
  const [activityIds, setActivityIds] = useState([]);
  const [hideEmpty, setHideEmpty] = useState(true);

  const [dailyMinutes, setDailyMinutes] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [absencesByUserId, setAbsencesByUserId] = useState({});

  const [expanded, setExpanded] = useState(() => new Set());
  const [details, setDetails] = useState({});

  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [exportingAbsencesFor, setExportingAbsencesFor] = useState(null);

  const activityKey = activityIds.slice().sort().join(',');
  const detailKey = useCallback((projectId) => `${projectId}|${yearMonth}|${activityKey}`, [yearMonth, activityKey]);

  // ---- Caricamenti ----
  useEffect(() => {
    if (currentRole === null || currentRole === undefined || !userId) return undefined;
    // Se ruolo o utente cambiano mentre la richiesta è in corso, la risposta vecchia viene ignorata
    let cancelled = false;
    setProjectsLoading(true);
    fetchVisibleProjects({ seesAllProjects: isDM, seesManagedProjects, userId })
      .then((list) => !cancelled && setProjects(list.sort((a, b) => a.name.localeCompare(b.name))))
      .catch((e) => !cancelled && setError(`Unable to load projects: ${e.message}`))
      .finally(() => !cancelled && setProjectsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [currentRole, userId, isDM, seesManagedProjects]);

  useEffect(() => {
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then((res) => res.json())
      .then((data) => setActivities(excludeForecastActivities(data.activities || [])))
      .catch(() => setActivities([]));
  }, []);

  const selectedProjects = useMemo(
    () => (projectIds.length ? projects.filter((p) => projectIds.includes(p.id)) : projects),
    [projects, projectIds],
  );
  const selectedKey = selectedProjects.map((p) => p.id).join(',');

  useEffect(() => {
    if (projectsLoading) return undefined;
    let cancelled = false;
    setSummaryLoading(true);
    setError(null);
    fetchProjectDailyMinutes(yearMonth, selectedProjects.map((p) => p.id), activityIds)
      .then((data) => !cancelled && setDailyMinutes(data))
      .catch((e) => !cancelled && setError(`Unable to load hours: ${e.message}`))
      .finally(() => !cancelled && setSummaryLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsLoading, yearMonth, selectedKey, activityKey]);

  useEffect(() => {
    getAllAbsencesForMonth(yearMonth)
      .then((entries) => {
        const byUser = {};
        entries.forEach((a) => {
          const uid = String(a.user_id);
          if (!byUser[uid]) byUser[uid] = new Map();
          byUser[uid].set(parseInt(String(a.date).slice(8, 10), 10), a);
        });
        setAbsencesByUserId(byUser);
      })
      .catch(() => setAbsencesByUserId({}));
  }, [yearMonth]);

  // Dettaglio persone dei progetti aperti (ricaricato al cambio di mese o attività)
  const loadDetail = useCallback(
    (projectId) => {
      const key = detailKey(projectId);
      setDetails((d) => ({ ...d, [projectId]: { key, loading: true } }));
      return fetchProjectPeople(projectId, year, month, activityIds)
        .then((res) => {
          setDetails((d) => (d[projectId]?.key === key ? { ...d, [projectId]: { key, ...res } } : d));
          return res;
        })
        .catch((e) => {
          setDetails((d) => (d[projectId]?.key === key ? { ...d, [projectId]: { key, error: `Unable to load people: ${e.message}` } } : d));
          return null;
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detailKey, year, month, activityKey],
  );

  useEffect(() => {
    expanded.forEach((pid) => {
      if (details[pid]?.key !== detailKey(pid)) loadDetail(pid);
    });
  }, [expanded, details, detailKey, loadDetail]);

  // ---- Righe tabella ----
  const allRows = useMemo(
    () =>
      selectedProjects.map((p) => {
        const days = dailyMinutes[p.id] || {};
        return {
          id: p.id,
          name: p.name,
          customer: p.customer?.name || null,
          project: p,
          days,
          total: Object.values(days).reduce((a, b) => a + b, 0),
        };
      }),
    [selectedProjects, dailyMinutes],
  );
  const rows = hideEmpty ? allRows.filter((r) => r.total > 0) : allRows;
  const hiddenCount = allRows.length - rows.length;

  const stats = useMemo(() => {
    const total = allRows.reduce((s, r) => s + r.total, 0);
    let peakDay = null;
    monthDays.forEach((d) => {
      const m = allRows.reduce((s, r) => s + (r.days[d.day] || 0), 0);
      if (m > 0 && (!peakDay || m > peakDay.minutes)) peakDay = { ...d, minutes: m };
    });
    return { total, withHours: allRows.filter((r) => r.total > 0).length, peakDay };
  }, [allRows, monthDays]);

  // ---- Azioni ----
  const changePeriod = (y, m) => {
    setYear(y);
    setMonth(m);
    setSearchParams({ year: String(y), month: String(m) }, { replace: true });
  };

  const toggleProject = (projectId) =>
    setExpanded((set) => {
      const next = new Set(set);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });

  const resetFilters = () => {
    setProjectIds([]);
    setActivityIds([]);
    setHideEmpty(true);
    setExpanded(new Set());
    changePeriod(now.getFullYear(), now.getMonth() + 1);
  };

  const exportProjectAbsences = (row, detail) => {
    setExportingAbsencesFor(row.id);
    try {
      downloadAbsenceCalendarWorkbook({
        projectName: row.name,
        year,
        month,
        members: sortMembersForCalendar(detail.projectUsers, detail.teamMembershipByUsername),
        absencesByUserId,
      });
    } catch (e) {
      setError(`Export failed: ${e.message}`);
    } finally {
      setExportingAbsencesFor(null);
    }
  };

  const bulkAbsenceProjects = isDM ? projects : projects.filter((p) => isManagerForProject(p, isAdmin));

  const exportHours = async (withPeople) => {
    setExportAnchor(null);
    setExporting(withPeople ? 'people' : 'projects');
    try {
      let peopleRows = [];
      if (withPeople) {
        const withHours = allRows.filter((r) => r.total > 0);
        const results = await Promise.all(
          withHours.map((r) =>
            details[r.id]?.key === detailKey(r.id) && details[r.id].people
              ? details[r.id]
              : fetchProjectPeople(r.id, year, month, activityIds),
          ),
        );
        peopleRows = withHours.flatMap((r, i) =>
          results[i].people.filter((p) => p.total > 0).map((p) => ({ labels: [r.name, p.name, p.team || ''], days: p.days })),
        );
      }
      downloadHoursWorkbook({
        year,
        month,
        monthDays,
        projectRows: rows.map((r) => ({ labels: [r.name, r.customer || ''], days: r.days })),
        peopleRows,
      });
    } catch (e) {
      setError(`Export failed: ${e.message}`);
    } finally {
      setExporting(null);
    }
  };

  const exportAllAbsences = async () => {
    setExportAnchor(null);
    setExporting('absences');
    try {
      await downloadAllProjectsAbsenceCalendarWorkbook({
        projects: bulkAbsenceProjects,
        year,
        month,
        absencesByUserId,
        scopeLabel: 'All_projects',
      });
    } catch (e) {
      setError(`Export failed: ${e.message}`);
    } finally {
      setExporting(null);
    }
  };

  const loading = projectsLoading || summaryLoading;
  const projectOptions = projects.map((p) => ({ id: p.id, name: p.name, caption: p.customer?.name }));
  const activityOptions = activities.map((a) => ({ id: a.id, name: a.name }));

  return (
    <PageContainer>
      <PageHeader title={'Projects overview'}>
        <MonthPicker year={year} month={month} onChange={changePeriod} />
      </PageHeader>

      {/* Filtri + azioni */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <MultiSelectFilter
          label="Projects"
          allLabel="All projects"
          icon={<ProjectIcon />}
          options={projectOptions}
          value={projectIds}
          onChange={setProjectIds}
          disabled={projectsLoading}
          width={300}
        />
        <MultiSelectFilter
          label="Activities"
          allLabel="All activities"
          icon={<ActivityIcon />}
          options={activityOptions}
          value={activityIds}
          onChange={setActivityIds}
          width={260}
        />
        <TogglePill label="Hide projects without hours" checked={hideEmpty} onChange={setHideEmpty} />
        <Button variant="text" color="inherit" onClick={resetFilters} sx={{ ...barButtonSx, color: MUTED_TEXT, fontWeight: 500 }}>
          Reset
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          color="inherit"
          startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon fontSize="small" />}
          endIcon={<ArrowDropDownIcon />}
          disabled={!!exporting || loading}
          onClick={(e) => setExportAnchor(e.currentTarget)}
          sx={barButtonSx}
        >
          Export
        </Button>
        <Menu
          anchorEl={exportAnchor}
          open={!!exportAnchor}
          onClose={() => setExportAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => exportHours(false)}>
            <ListItemIcon>
              <TableIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Hours by project" secondary="Excel, current filters" />
          </MenuItem>
          <MenuItem onClick={() => exportHours(true)}>
            <ListItemIcon>
              <TableIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Hours by project and person" secondary="Excel, two sheets" />
          </MenuItem>
          {bulkAbsenceProjects.length > 0 && (
            <MenuItem onClick={exportAllAbsences}>
              <ListItemIcon>
                <EventBusyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Absences, all my projects" secondary="Excel, one sheet per project" />
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Riepilogo */}
      {!loading && allRows.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          <strong style={{ color: 'var(--rt-heading)' }}>{formatHours(stats.total)} h</strong> tracked ·{' '}
          {stats.withHours} of {allRows.length} projects with hours
          {stats.peakDay && (
            <>
              {' '}
              · busiest day {stats.peakDay.label} {stats.peakDay.day} ({formatHours(stats.peakDay.minutes)} h)
            </>
          )}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && rows.length === 0 ? (
        <Alert severity="info">
          {allRows.length === 0
            ? 'No projects available for your role.'
            : 'No hours tracked on the selected projects in this month.'}
        </Alert>
      ) : (
        <MonthlyPivotTable
          monthDays={monthDays}
          rows={rows}
          loading={loading}
          expanded={expanded}
          onToggle={toggleProject}
          details={details}
          absencesByUserId={absencesByUserId}
          onExportAbsences={exportProjectAbsences}
          exportingAbsencesFor={exportingAbsencesFor}
        />
      )}

      {/* Legenda */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 1.5, color: 'text.secondary' }}>
        <Typography variant="caption">Click a project to see its teams, and a team to see its people.</Typography>
        <Legend color="rgba(90,176,49,0.3)" label="More hours that day" />
        <Legend color="rgba(229,57,53,0.12)" label="Full-day absence" />
        <Legend color="rgba(249,168,37,0.18)" label="Partial absence" />
        <Legend color="rgba(239,108,0,0.16)" label="Person over 8h" />
        {hiddenCount > 0 && (
          <Typography variant="caption">
            {hiddenCount} project{hiddenCount === 1 ? '' : 's'} without hours hidden
          </Typography>
        )}
      </Box>
    </PageContainer>
  );
}

function Legend({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: color, border: '1px solid var(--rt-hairline)' }} />
      <Typography variant="caption">{label}</Typography>
    </Box>
  );
}
